import { createRoot } from "react-dom/client";

import {
  APP_INIT_ERROR,
  APP_READY,
  getConfig,
  initialize,
  subscribe
} from "@edx/frontend-platform";
import { ErrorPage } from "@edx/frontend-platform/react";
import { isLearningDemoMode } from "@/demo/isLearningDemoMode";
import { post } from "@/api/httpClient";

import App from "./App";

subscribe(APP_READY, () => {
  const root = createRoot(document.getElementById("root")!);

  root.render(<App />);
});

subscribe(APP_INIT_ERROR, (error?: unknown) => {
  if (isLearningDemoMode()) {
    // In demo mode (including static S3 hosting), platform init errors are
    // non-fatal — all API calls are intercepted client-side so the app can
    // still render even without real platform services.
    console.warn("[learning-demo] Platform init error (non-blocking):", error);
    const root = createRoot(document.getElementById("root")!);
    root.render(<App />);
    return;
  }

  console.error("Failed to initialize OpenEdX platform:", error);
  const root = createRoot(document.getElementById("root")!);
  const message =
    error && typeof error === "object" && "message" in error
      ? String(error.message)
      : "Unknown error";
  root.render(<ErrorPage message={message} />);
});

const demoMode = isLearningDemoMode();

const setDemoJwtCookie = () => {
  const encodeBase64Url = (value: string) => {
    const encoded = btoa(value).split("+").join("-").split("/").join("_");
    return encoded.split("=").join("");
  };

  const tokenHeader = encodeBase64Url(
    JSON.stringify({
      alg: "none",
      typ: "JWT"
    })
  );

  const tokenPayload = encodeBase64Url(
    JSON.stringify({
      exp: 4102444800,
      user_id: 999999,
      preferred_username: "demo.student",
      email: "demo.student@euka.edu.au",
      name: "Demo Student",
      roles: ["learner"]
    })
  );

  const jwtCookieName = "edx-jwt-cookie-header-payload";
  document.cookie = `${jwtCookieName}=${tokenHeader}.${tokenPayload}.demo-signature; path=/; SameSite=Lax`;
};

if (demoMode) {
  setDemoJwtCookie();

  // Expose a console helper so reviewers can call demo-only POST endpoints
  // without going through a raw fetch (which bypasses the httpClient intercept).
  // Usage: __demoPost('/demo/toggle-g11-assessment')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__demoPost = (endpoint: string, payload?: unknown) =>
    post(endpoint, payload).then(res => {
      console.log(`[demo] POST ${endpoint} →`, res);
      return res;
    });
}

const handlers: {
  config: () => ReturnType<typeof getConfig>;
  auth?: () => void;
  analytics?: () => void;
} = {
  config: () => getConfig()
};

if (demoMode) {
  handlers.auth = () => {};
  handlers.analytics = () => {};
}

initialize({
  handlers,
  messages: [],
  requireAuthenticatedUser: !demoMode,
  hydrateAuthenticatedUser: !demoMode
});
